import { useEffect, useRef, useState } from "react";

import { useBookingEditModeStore } from "../booking_edit_mode_store";

// Keeps returning the last pending edit value ("sticky overlay") after clearEdit
// until the server value catches up, preventing flashes of intermediate data
// (e.g. 12h instead of 4h) caused by async refetch chain delays.
// Falls back to clearing the overlay after STICKY_TIMEOUT_MS if convergence never happens.
const STICKY_TIMEOUT_MS = 10000;

export const usePendingEditValue = (
  rowKey: string,
  date: string,
  serverValue?: number | null
): number | undefined => {
  const storeValue = useBookingEditModeStore((state) => {
    const key = `${rowKey}:${date}`;
    return state.smartPlannerMode.pendingEdits[key]?.value;
  });

  const [stickyValue, setStickyValue] = useState<number | undefined>(undefined);
  const stickyTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const hasActiveEdit = storeValue !== undefined;
  const hasSticky = stickyValue !== undefined;
  const editWasCleared = !hasActiveEdit && hasSticky;
  const bothEmpty = !serverValue && !stickyValue;
  const serverCaughtUp = serverValue === stickyValue || bothEmpty;

  if (hasActiveEdit && storeValue !== stickyValue) {
    setStickyValue(storeValue);
  } else if (editWasCleared && serverCaughtUp) {
    setStickyValue(undefined);
  }

  useEffect(() => {
    const needsFallbackTimeout = editWasCleared && !serverCaughtUp;

    if (needsFallbackTimeout && stickyTimeoutRef.current === undefined) {
      stickyTimeoutRef.current = setTimeout(() => {
        setStickyValue(undefined);
        stickyTimeoutRef.current = undefined;
      }, STICKY_TIMEOUT_MS);
    } else if (!needsFallbackTimeout && stickyTimeoutRef.current !== undefined) {
      clearTimeout(stickyTimeoutRef.current);
      stickyTimeoutRef.current = undefined;
    }

    return () => {
      if (stickyTimeoutRef.current !== undefined) {
        clearTimeout(stickyTimeoutRef.current);
      }
    };
  }, [editWasCleared, serverCaughtUp]);

  if (hasActiveEdit) {
    return storeValue;
  }
  if (editWasCleared && !serverCaughtUp) {
    return stickyValue;
  }
  return undefined;
};
